/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import { useState } from 'react';
import { AlertColor } from '@mui/material';
import { isEmpty } from 'lodash';
import MasterDataApiService from '../../../../../../Shared/Services/MasterDataService';
import { CreateServiceDto, CreateTaskDto, CreateTaskSubtaskMappingDto } from '../../../../../../Shared/Models';
import AllJobsApiService from '../../../../../Jobs/Services/JobManagementService';

export const useServiceOperations = (userId: string, showSnackbar: (severity: AlertColor, message: string) => void) => {
  const [isCreateServiceLoading, setIsCreateServiceLoading] = useState(false);
  const [isTaskCreationLoading, setIsTaskCreationLoading] = useState(false);
  const [isTaskSubTaskMapLoading, setIsTaskSubTaskMapLoading] = useState(false);

  const fetchScheduleMasterServices = async (scheduleId: string) => {
    try {
      const response = await AllJobsApiService.getMasterServices(scheduleId);
      return response?.data || null;
    } catch (error) {
      console.error('Error fetching schedule services:', error);
      showSnackbar('error', 'Failed to fetch services');
      return null;
    }
  };

  const handleCreateTask = async (taskName: string, taskDescription: string): Promise<CreateTaskDto | undefined> => {
    setIsTaskCreationLoading(true);
    try {
      const response = await MasterDataApiService.createTask(userId, taskName, taskDescription, 0, 'unit');
      if (!isEmpty(response?.data)) {
        showSnackbar('success', `Task - ${response?.data?.[0]?.taskName} got created successfully`);
        return {
          mdTaskId: response?.data?.[0]?.taskId,
          mdTaskName: response?.data?.[0]?.taskName,
          mdTaskDescription: response?.data?.[0]?.taskDescription,
        };
      }
    } catch (error) {
      console.error('Error creating task:', error);
      showSnackbar('error', 'Failed to create task');
    } finally {
      setIsTaskCreationLoading(false);
    }
    return undefined;
  };

  const handleServiceTaskMapping = async (serviceTaskMapping: any) => {
    if (!serviceTaskMapping) {
      console.error('ServiceTaskMapping is undefined');
      return { success: false };
    }

    setIsTaskSubTaskMapLoading(true);
    try {
      const response = await MasterDataApiService.serviceTaskMapping(userId, serviceTaskMapping);
      if (response && response?.data) {
        if (response?.status === 200 || response?.status === 201 || response?.status === 204) {
          showSnackbar('success', 'Service task mapping created successfully');
        } else {
          showSnackbar('error', 'Something went wrong, please try again.');
        }
        return { success: true, data: response.data };
      }
    } catch (error) {
      console.error('Error in service task mapping:', error);
      showSnackbar('error', 'Failed to create service task mapping');
    } finally {
      setIsTaskSubTaskMapLoading(false);
    }
    return { success: false };
  };

  const handleCreateService = async (createServicePayload: CreateServiceDto[]) => {
    if (!createServicePayload) {
      console.error('CreateServicePayload is undefined');
      return undefined;
    }

    setIsCreateServiceLoading(true);
    try {
      const response = await MasterDataApiService.createService(userId, createServicePayload);
      if (!isEmpty(response?.data)) {
        return {
          mdServiceId: response?.data?.[0]?.serviceCategoryId,
          serviceName: response?.data?.[0]?.serviceCategoryName,
          mdServiceName: response?.data?.[0]?.serviceCategoryName,
          serviceDescription: response?.data?.[0]?.serviceCategoryDescription,
          mdServiceDescription: response?.data?.[0]?.serviceCategoryDescription,
        };
      }
    } catch (error) {
      console.error('Error creating service:', error);
      showSnackbar('error', 'Failed to create service');
    } finally {
      setIsCreateServiceLoading(false);
    }
    return undefined;
  };

  const handleTaskSubTaskMapping = async (taskSubTaskPayload: CreateTaskSubtaskMappingDto) => {
    if (!taskSubTaskPayload) {
      console.error('TaskSubTaskPayload is undefined');
      return { success: false };
    }

    setIsTaskSubTaskMapLoading(true);
    try {
      const response = await MasterDataApiService.taskSubTaskMapping(userId, taskSubTaskPayload);
      if (response && response?.data) {
        const status = response?.data?.tasks?.[0]?.subTasks?.[0]?.status;
        if (status === 'ADDED') {
          showSnackbar('success', 'Task-SubTask mapping created successfully');
          return { success: true, data: response.data };
        } else {
          showSnackbar('error', response?.data?.tasks?.[0]?.subTasks?.[0]?.message || 'Mapping failed');
        }
      }
    } catch (error) {
      console.error('Error in task-subtask mapping:', error);
      showSnackbar('error', 'Failed to create task-subtask mapping');
    } finally {
      setIsTaskSubTaskMapLoading(false);
    }
    return { success: false };
  };

  return {
    isCreateServiceLoading,
    isTaskCreationLoading,
    isTaskSubTaskMapLoading,

    fetchScheduleMasterServices,
    handleCreateTask,
    handleServiceTaskMapping,
    handleCreateService,
    handleTaskSubTaskMapping,
  };
};
